import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#syncBottomMenuOffset();
    window.addEventListener('resize', this.#onWindowResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    window.removeEventListener('resize', this.#onWindowResize);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    this.#syncBottomMenuOffset();

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    if (!details.hasAttribute('open')) {
      details.setAttribute('open', '');
    }
    details.classList.add('menu-closing');
    details.classList.remove('menu-open');

    // Wait for the drawer transition before resetting open state.
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
    const transitionTarget = drawer || details;

    const finishClose = () => {
      details.classList.remove('menu-closing');
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
    };

    requestAnimationFrame(() => {
      const computed = getComputedStyle(transitionTarget);
      const durations = computed.transitionDuration
        .split(',')
        .map((value) => value.trim())
        .map((value) => {
          if (value.endsWith('ms')) return parseFloat(value);
          if (value.endsWith('s')) return parseFloat(value) * 1000;
          return 0;
        });
      const maxDuration = Math.max(...durations, 0);

      if (maxDuration <= 0) {
        finishClose();
        return;
      }

      let closed = false;
      const cleanup = () => {
        transitionTarget.removeEventListener('transitionend', handleTransitionEnd);
      };
      const complete = () => {
        if (closed) return;
        closed = true;
        cleanup();
        finishClose();
      };
      const handleTransitionEnd = (event) => {
        if (event.target !== transitionTarget) return;
        if (event.propertyName !== 'transform' && event.propertyName !== 'opacity') return;
        complete();
      };

      transitionTarget.addEventListener('transitionend', handleTransitionEnd);
      setTimeout(complete, maxDuration + 80);
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  #onWindowResize = () => {
    this.#syncBottomMenuOffset();
  };

  #syncBottomMenuOffset() {
    const drawer = this.querySelector('.menu-drawer');
    if (!(drawer instanceof HTMLElement)) return;

    const bottomMenu = drawer.querySelector('.menu-drawer__bottom-menu');
    if (!(bottomMenu instanceof HTMLElement)) {
      drawer.style.setProperty('--menu-drawer-bottom-menu-offset', '0px');
      return;
    }

    const menuHeight = Math.ceil(bottomMenu.getBoundingClientRect().height) * 2;
    drawer.style.setProperty('--menu-drawer-bottom-menu-offset', `${menuHeight}px`);
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.classList.remove('menu-closing');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
